import React, { useEffect, useState } from 'react';

import { Form } from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import { Alert, Box, Button, Dialog, DialogContent, Grid } from '@mui/material';

import { getRegionByCurrency } from '@luxuryescapes/lib-regions';

import { PROMO_DEVICE_TYPES } from '~/consts/promo';

import { usePromo } from '~/hooks/usePromo';

import { CreateOrUpdatePromoPayload, InternalPromoWithDiscounts } from '~/services/PromoService';
import { addDays, formatDateLongISO } from '~/services/TimeService';

import Spinner from '../Common/Spinner';
import PromoCheckStatus from '../PromoV3/PromoCheckStatus';
import { CategoryTagSelector, DepartmentTagSelector, SubCategoryTagSelector } from '../PromoV3/PromoTagSelectors';

interface Props {
  isModalOpen: boolean;
  closeModal: () => void;
  user: App.User;
  onPromoCodeCreated?: (promoCode: string) => void;
}

interface QuickPromoPayload {
  promoData: Partial<InternalPromoWithDiscounts>;
  discountData: {
    discount_value: number;
    region: string;
  };
}

const baseSchema: RJSFSchema = {
  title: 'Promo Code',
  type: 'object',
  properties: {
    code_name: {
      type: 'string',
      title: 'Code',
    },
    promo_type: {
      type: 'string',
      title: 'Promo Type',
      enum: ['fixed_amount', 'percentage'],
    },
    expires_at: {
      type: 'string',
      format: 'date-time',
      title: 'Expiry date',
      default: formatDateLongISO(addDays(365)),
    },
    description: {
      type: 'string',
      title: 'Description',
    },
    case_id: {
      type: 'string',
      title: 'Case ID',
    },
    currency: {
      type: 'string',
      title: 'Currency',
      enum: [
        'AUD',
        'CAD',
        'CNY',
        'EUR',
        'HKD',
        'INR',
        'IDR',
        'ILS',
        'JPY',
        'KRW',
        'MOP',
        'MYR',
        'NZD',
        'PHP',
        'QAR',
        'RUB',
        'SAR',
        'SGD',
        'TWD',
        'THB',
        'ZAR',
        'AED',
        'GBP',
        'USD',
        'VND',
      ],
    },
    discount_value: {
      type: 'number',
      title: 'Value',
    },
  },
  required: ['code_name', 'description', 'currency', 'promo_type', 'discount_value', 'expires_at', 'case_id'],
};

function CreatePromoForUserModal({ isModalOpen, closeModal, user, onPromoCodeCreated }: Props) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [formData, setFormData] = useState<{
    promo_type: string;
    dept_tag?: string;
    category_tag?: string;
    sub_category_tag?: string;
    [key: string]: any;
  }>({
    promo_type: 'fixed_amount',
  });
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const { enqueueSnackbar } = useSnackbar();

  const {
    errors,
    promoCheckStatus,
    createPromo,
    checkPromo,
    initPromoData,
    promoTagArrays,
    isPromoMetaLoading,
    setPromoTagSelection,
    promoTagSelections,
  } = usePromo({
    options: {
      autoSelectTag: true,
    },
    onPromoUpdate: async (promo) => {
      if (!promo) return;
      let message = `Promo code created`;
      try {
        message += ' and copied to clipboard!';
      } catch (e) {
        message += '.';
      }
      enqueueSnackbar(message, {
        variant: 'success',
      });
      if (onPromoCodeCreated) {
        onPromoCodeCreated(promo.code_name);
      }
    },
  });

  const formSchema = (): RJSFSchema => {
    return baseSchema;
  };

  const handleFormChange = (evt) => {
    const currentFormData = evt.formData as any;

    // Check which field actually changed by comparing with current form data
    const currentForm = formData as any;

    // Handle department tag selection
    if (currentFormData.dept_tag && currentFormData.dept_tag !== currentForm?.dept_tag) {
      setPromoTagSelection(currentFormData.dept_tag, null, null);
      return; // Exit early, let useEffect update form data
    }

    // Handle category tag selection
    if (currentFormData.category_tag && currentFormData.category_tag !== currentForm?.category_tag) {
      setPromoTagSelection(
        currentFormData.dept_tag || promoTagSelections.selectedDepartment,
        currentFormData.category_tag,
        null,
      );
      return; // Exit early, let useEffect update form data
    }

    // Handle sub-category tag selection - use form data like category selection
    if (currentFormData.sub_category_tag && currentFormData.sub_category_tag !== currentForm?.sub_category_tag) {
      setPromoTagSelection(
        currentFormData.dept_tag, // Use form data like we do for category
        currentFormData.category_tag, // Use form data like we do for category
        currentFormData.sub_category_tag,
      );
      return;
    }

    if (currentFormData?.code_name) {
      checkPromo({
        codeName: currentFormData.code_name,
        brand: 'luxuryescapes',
      });
    }

    // Only update form data for non-tag changes
    setFormData(evt.formData);
  };

  useEffect(() => {
    if (hasLoaded) {
      return;
    }
    initPromoData(null);
    setHasLoaded(true);
  }, []);

  // Sync promoTagSelections to form data, but only when hook state actually changes
  useEffect(() => {
    if (!formData || !promoTagSelections) {
      return;
    }

    const currentFormData = formData as any; // Type assertion for dynamic form data
    const updatedFormData = {
      ...currentFormData,
      dept_tag: promoTagSelections.selectedDepartment || undefined,
      category_tag: promoTagSelections.selectedCategory || undefined,
      sub_category_tag: promoTagSelections.selectedSubCategory || undefined,
    };

    // Only update if there are actual changes to avoid infinite loops
    if (
      updatedFormData.dept_tag !== currentFormData.dept_tag ||
      updatedFormData.category_tag !== currentFormData.category_tag ||
      updatedFormData.sub_category_tag !== currentFormData.sub_category_tag
    ) {
      setFormData(updatedFormData);
    }
  }, [promoTagSelections]); // Only depend on promoTagSelections, not formData

  const onSubmit = async (form) => {
    const { code_name, currency, promo_type, discount_value, description, expires_at, case_id } = form.formData;

    // Get tag values from formData state since they're handled by custom components
    const { dept_tag, category_tag, sub_category_tag } = formData;

    try {
      const quickUpdatePromo: CreateOrUpdatePromoPayload = {
        promo: {
          code_name,
          promo_type,
          description,
          expires_at,
          code_limit: 1,
          brand: brand as QuickPromoPayload['promoData']['brand'],
          email_addresses: [user.email],
          device_types: PROMO_DEVICE_TYPES.filter((device) => device.key !== 'all').map((item) => item.key) as Array<
            'web' | 'ios' | 'android'
          >,
          limit_per_user: 1,
          limit_per_user_per_year: 0,
          department_tag: dept_tag,
          case_id,
          dept_tag,
          category_tag,
          sub_category_tag,
        },
        promoDiscount: {
          discount_value,
          region: getRegionByCurrency(currency).code as CreateOrUpdatePromoPayload['promoDiscount']['region'],
          products: ['hotel-ALL', 'tour-ALL', 'cruise-ALL'],
        },
        mode: 'create-only',
      };

      await createPromo(quickUpdatePromo);
      closeModal();
    } catch (e) {
      enqueueSnackbar(`${brand}: ${JSON.stringify(e, null, 2)}`, { variant: 'error' });
    }
  };

  if (isPromoMetaLoading) {
    return <Spinner />;
  }

  return (
    <Dialog open={isModalOpen} onClose={closeModal}>
      <DialogContent>
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" onClick={closeModal}>
            X
          </Button>
        </Box>
        <PromoCheckStatus promoCheckStatus={promoCheckStatus} />
        {errors && errors.length > 0 && <Alert severity="error">{JSON.stringify(errors, null, 2)}</Alert>}
        {!isPromoMetaLoading && (
          <>
            <Form
              schema={formSchema()}
              onChange={handleFormChange}
              onSubmit={onSubmit}
              validator={validator}
              formData={formData}
            >
              <Grid container spacing={1} marginTop={1}>
                <DepartmentTagSelector
                  promoTagArrays={promoTagArrays}
                  promoTagSelections={promoTagSelections}
                  setPromoTagSelection={setPromoTagSelection}
                  isPromoMetaLoading={isPromoMetaLoading}
                  formData={formData}
                  onFormChange={handleFormChange}
                  formType="rjsf"
                />
                <CategoryTagSelector
                  promoTagArrays={promoTagArrays}
                  promoTagSelections={promoTagSelections}
                  setPromoTagSelection={setPromoTagSelection}
                  isPromoMetaLoading={isPromoMetaLoading}
                  formData={formData}
                  onFormChange={handleFormChange}
                  formType="rjsf"
                />
                <SubCategoryTagSelector
                  promoTagArrays={promoTagArrays}
                  promoTagSelections={promoTagSelections}
                  setPromoTagSelection={setPromoTagSelection}
                  isPromoMetaLoading={isPromoMetaLoading}
                  formData={formData}
                  onFormChange={handleFormChange}
                  formType="rjsf"
                />
              </Grid>
              <Grid item gap={2} marginTop={2}>
                <Button type="submit" variant="contained" fullWidth>
                  Create
                </Button>
              </Grid>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CreatePromoForUserModal;
